import { createContext, useContext, useEffect, useState } from "react";

const ACCENTS = {
  green: { hsl: "142 34% 46%", hex: "#4f9d6b", hi: "142 55% 82%", bright: "142 44% 52%", deep: "142 36% 37%" },
  blue: { hsl: "212 48% 55%", hex: "#4f86c6", hi: "212 70% 86%", bright: "212 60% 60%", deep: "212 50% 45%" },
  gold: { hsl: "43 66% 45%", hex: "#c9a227", hi: "46 90% 80%", bright: "44 82% 53%", deep: "40 72% 36%" },
};
const ACCENT_ORDER = ["gold", "green", "blue"];
const ACCENT_KEY = "clan-accent";
const MODE_KEY = "clan-theme-mode";
const MODES = ["parchment", "dark"];
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [accent, setAccent] = useState("gold");
  const [mode, setMode] = useState("parchment");

  useEffect(() => {
    const storedAccent = typeof window !== "undefined" ? window.localStorage.getItem(ACCENT_KEY) : null;
    if (storedAccent && ACCENTS[storedAccent]) setAccent(storedAccent);
    const storedMode = typeof window !== "undefined" ? window.localStorage.getItem(MODE_KEY) : null;
    if (storedMode && MODES.includes(storedMode)) setMode(storedMode);
  }, []);

  useEffect(() => {
    const a = ACCENTS[accent];
    const root = document.documentElement;
    root.style.setProperty("--accent-hsl", a.hsl);
    root.style.setProperty("--accent", a.hex);
    root.style.setProperty("--accent-sheen-hi", a.hi);
    root.style.setProperty("--accent-bright-hsl", a.bright);
    // Primary action colour + scrollbar/selection now follow the selected accent.
    root.style.setProperty("--primary-hsl", a.hsl);
    root.style.setProperty("--primary-hover-hsl", a.deep);
    root.style.setProperty("--primary", a.hex);
  }, [accent]);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "dark") root.setAttribute("data-theme-mode", "dark");
    else root.removeAttribute("data-theme-mode");
  }, [mode]);

  const toggle = () =>
    setAccent((p) => {
      const next = ACCENT_ORDER[(ACCENT_ORDER.indexOf(p) + 1) % ACCENT_ORDER.length];
      window.localStorage.setItem(ACCENT_KEY, next);
      return next;
    });

  const toggleMode = () =>
    setMode((p) => {
      const next = p === "parchment" ? "dark" : "parchment";
      window.localStorage.setItem(MODE_KEY, next);
      return next;
    });

  return (
    <ThemeContext.Provider
      value={{ accent, toggle, mode, toggleMode }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
